import { createContext, useContext } from "react";
import type { User } from "firebase/auth";

/**
 * Who is signed in.
 *
 * Firebase issues the identity and the API verifies it, so the only job of
 * this layer is to hold the current user, keep the API client's bearer token
 * fresh, and expose sign in and sign out. The provider that does that lives
 * in AuthProvider.tsx; everything here is the types and the readers.
 */

export interface SignedInUser {
  /** Best available display name. Falls back to the email local part. */
  name: string;
  email: string | null;
  uid: string;
}

export interface AuthState {
  user: SignedInUser | null;
  /** True until Firebase has said whether there is a session. */
  loading: boolean;
  /** False when the build was given no Firebase project. */
  configured: boolean;
  signIn(email: string, password: string): Promise<void>;
  signOut(): Promise<void>;
  resetPassword(email: string): Promise<void>;
}

export const AuthContext = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth used outside AuthProvider");
  return ctx;
}

/** "jessica.ronane@1v1.com" reads better as "Jessica Ronane" in a greeting. */
export function fromEmail(email: string): string {
  const local = email.includes("@") ? email.split("@")[0] : email;
  const words = local.split(/[._-]+/).filter(Boolean);
  if (words.length === 0) return email;
  return words.map((w) => w[0].toUpperCase() + w.slice(1)).join(" ");
}

export function toUser(u: User): SignedInUser {
  const email = u.email?.trim() || null;
  return {
    name: u.displayName?.trim() || (email ? fromEmail(email) : "Signed in"),
    email,
    uid: u.uid,
  };
}

/** Firebase error codes turned into something a manager can act on. */
export function signInMessage(code: string): string {
  switch (code) {
    case "auth/invalid-email":
      return "That does not look like an email address.";
    case "auth/missing-password":
      return "Enter your password.";
    // Firebase returns one code for a wrong password and an unknown account,
    // deliberately, so the form cannot be used to discover who has an
    // account. The message has to stay just as vague.
    case "auth/invalid-credential":
    case "auth/wrong-password":
    case "auth/user-not-found":
      return "That email and password do not match an account.";
    // Only seen if the Email/Password provider gets switched off in the
    // console, which looks identical to a bad password from the form.
    case "auth/operation-not-allowed":
      return "Email sign in is turned off for this project. That is a console setting, not your password.";
    case "auth/user-disabled":
      return "That account has been turned off. Ask an admin to re-enable it.";
    case "auth/too-many-requests":
      return "Too many attempts. Wait a few minutes and try again.";
    case "auth/network-request-failed":
      return "Could not reach the sign in service. Check your connection.";
    case "auth/invalid-api-key":
    case "auth/api-key-not-valid.-please-pass-a-valid-api-key.":
      return "This build has the wrong Firebase key. That is a config problem, not your password.";
    default:
      return "Could not sign you in. Try again.";
  }
}

/** First name, for the dashboard greeting. */
export const firstName = (u: SignedInUser): string => u.name.split(/\s+/)[0];
